import { EmailTemplateType } from '../Constant/email-template.constants';

// Mirrors ataserver's EmailService.build*ReportHtml() methods with fixed
// sample data, purely for an in-app visual preview -- not sent anywhere.
const SAMPLE = {
  project: 'Sample Project',
  suite: 'Login Flow Suite',
  executedBy: 'jane.doe',
  triggeredTime: '23-07-2026 10:15:00',
  reportLink: '#',
  outcome: 'Completed',
  duration: '2m 34s',
  totalScenarios: 12,
  totalSteps: 86,
};

// Each suite can run on several platforms, and each platform can iterate
// several times (e.g. data-driven repeats) -- platformRows holds one entry
// per (platform, iteration) pair, all grouped under the same suite row via
// rowspan in the Detailed/Simple tables below.
const SAMPLE_SUITES = [
  {
    suiteName: 'Login Flow Suite',
    features: 4,
    scenarios: 12,
    steps: 86,
    reportLink: '#',
    platformRows: [
      { platform: 'Chrome', iteration: '1', duration: '1m 12s', status: 'Passed', reason: 'Passed' },
      { platform: 'Chrome', iteration: '2', duration: '1m 05s', status: 'Passed', reason: 'Passed' },
      { platform: 'Firefox', iteration: '1', duration: '1m 22s', status: 'Passed', reason: 'Passed' },
    ],
  },
  {
    suiteName: 'Checkout Flow Suite',
    features: 2,
    scenarios: 6,
    steps: 34,
    reportLink: '#',
    platformRows: [{ platform: 'Safari', iteration: '1', duration: '48s', status: 'Failed', reason: 'Assertion failed on step 5' }],
  },
];

function resolveStatusColor(outcome: string): string {
  switch (outcome) {
    case 'Completed':
      return '#3a8bfe'; // app theme (brand) color, not a traffic-light green
    case 'Failed':
    case 'Aborted':
    case 'Timed Out':
    case 'Compilation Error':
      return '#e5484d';
    case 'Insufficient Credits':
    case 'Failed to Start':
      return '#f5a623';
    default:
      return '#3a8bfe';
  }
}

function buildDefaultPreview(): string {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>
      body { font-family: Arial, sans-serif; background-color: #f4f4f4; margin: 0; padding: 5px; font-size: 16px; }
      .container { background-color: #ffffff; padding: 20px; border-radius: 8px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
      .button { display: inline-block; padding: 10px 20px; font-size: 16px; text-decoration: none; background-color: #3a8bfe; color: #ffffff; border-radius: 5px; }
      a { pointer-events: none; cursor: default; }
    </style></head>
    <body>
      <div class="container">
        <p>Hello,</p>
        <p>We are pleased to inform you that the execution for ${SAMPLE.project} has been successfully completed. The report is now available.</p>
        <p>You initiated the execution at [${SAMPLE.triggeredTime}] for suite [${SAMPLE.suite}] Executed By : ${SAMPLE.executedBy}. You can access the report by clicking the button below:</p>
        <a href="${SAMPLE.reportLink}" class="button">Open Report</a>
        <p>If the button doesn't work, you can copy and paste the following URL into your browser:</p>
        <h4>${SAMPLE.reportLink}</h4>
        <p>Do not reply to this automated message.</p>
        <br>
        <p>Thank you,</p>
        <strong><span style="color:#3a89ff;">XPEER.AI</span></strong>
      </div>
    </body></html>`;
}

function buildMinimalPreview(): string {
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>a { pointer-events: none; cursor: default; }</style></head>
    <body style="font-family: Arial, sans-serif; color: #222222; padding: 16px; font-size: 15px; line-height: 1.6;">
      <p>Hello,</p>
      <p>Execution for <strong>${SAMPLE.project}</strong> (suite: ${SAMPLE.suite}) has completed.</p>
      <p>Initiated at [${SAMPLE.triggeredTime}] by ${SAMPLE.executedBy}.</p>
      <p>Report: <a href="${SAMPLE.reportLink}">${SAMPLE.reportLink}</a></p>
      <p>Do not reply to this automated message.</p>
      <p>&mdash; XPEER.AI</p>
    </body></html>`;
}

function buildDetailedPreview(): string {
  const rows = SAMPLE_SUITES.map((s) => {
    const rowCount = s.platformRows.length;
    return s.platformRows
      .map((p, i) => {
        const suiteCells =
          i === 0
            ? `<td rowspan="${rowCount}">${s.suiteName}</td><td rowspan="${rowCount}">${s.features}</td><td rowspan="${rowCount}">${s.scenarios}</td><td rowspan="${rowCount}">${s.steps}</td>`
            : '';
        const reportCell = i === 0 ? `<td rowspan="${rowCount}"><a href="${s.reportLink}" class="button">Open Report</a></td>` : '';
        return `<tr>${suiteCells}<td>${p.platform}</td><td>${p.iteration}</td><td>${p.duration}</td><td>${p.status}</td><td>${p.reason}</td>${reportCell}</tr>`;
      })
      .join('');
  }).join('');
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>
      body { font-family: Arial, sans-serif; background-color: #f4f4f4; margin: 0; padding: 16px; font-size: 14px; }
      .container { background-color: #ffffff; padding: 24px; border-radius: 8px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
      .meta { border-collapse: collapse; margin: 12px 0 20px; }
      .meta td { padding: 4px 12px 4px 0; }
      .meta td:first-child { color: #64748b; }
      .meta td:last-child { font-weight: 600; color: #0f3060; }
      table.results { width: 100%; border-collapse: collapse; font-size: 12.5px; }
      table.results th, table.results td { padding: 8px 10px; border: 1px solid #eaf2ff; text-align: left; white-space: nowrap; }
      table.results th { background-color: #f5f9ff; color: #0f3060; }
      table.results td[rowspan] { vertical-align: top; }
      .button { display: inline-block; padding: 6px 14px; font-size: 12.5px; text-decoration: none; background-color: #3a8bfe; color: #ffffff; border-radius: 5px; white-space: nowrap; }
      a { pointer-events: none; cursor: default; }
    </style></head>
    <body>
      <div class="container">
        <p>Hello,</p>
        <p>${SAMPLE_SUITES.length} suite executions for <strong>${SAMPLE.project}</strong> have finished. A detailed summary is below.</p>
        <table class="meta">
          <tr><td>Executed By</td><td>${SAMPLE.executedBy}</td></tr>
          <tr><td>Initiated At</td><td>${SAMPLE.triggeredTime}</td></tr>
        </table>
        <div style="overflow-x:auto;">
        <table class="results">
          <tr><th>Suite Name</th><th>Features</th><th>Scenarios</th><th>Steps</th><th>Execution Platform</th><th>Iteration</th><th>Duration</th><th>Status</th><th>Reason</th><th>Report</th></tr>
          ${rows}
        </table>
        </div>
        <p style="margin-top:20px;">Do not reply to this automated message.</p>
        <br>
        <p>Thank you,</p>
        <strong><span style="color:#3a89ff;">XPEER.AI</span></strong>
      </div>
    </body></html>`;
}

function buildSimplePreview(): string {
  const rows = SAMPLE_SUITES.map((s) => {
    const rowCount = s.platformRows.length;
    return s.platformRows
      .map((p, i) => {
        const suiteCell = i === 0 ? `<td rowspan="${rowCount}">${s.suiteName}</td>` : '';
        const reportCell = i === 0 ? `<td rowspan="${rowCount}"><a href="${s.reportLink}" class="button">Open Report</a></td>` : '';
        return `<tr>${suiteCell}<td>${p.platform}</td><td>${p.iteration}</td><td>${p.duration}</td><td>${p.status}</td>${reportCell}</tr>`;
      })
      .join('');
  }).join('');
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>
      body { font-family: Arial, sans-serif; background-color: #f4f4f4; margin: 0; padding: 16px; font-size: 14px; }
      .container { background-color: #ffffff; padding: 24px; border-radius: 8px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
      .meta { border-collapse: collapse; margin: 12px 0 20px; }
      .meta td { padding: 4px 12px 4px 0; }
      .meta td:first-child { color: #64748b; }
      .meta td:last-child { font-weight: 600; color: #0f3060; }
      table.results { width: 100%; border-collapse: collapse; font-size: 13px; }
      table.results th, table.results td { padding: 8px 10px; border: 1px solid #eaf2ff; text-align: left; white-space: nowrap; }
      table.results th { background-color: #f5f9ff; color: #0f3060; }
      table.results td[rowspan] { vertical-align: top; }
      .button { display: inline-block; padding: 6px 14px; font-size: 13px; text-decoration: none; background-color: #3a8bfe; color: #ffffff; border-radius: 5px; white-space: nowrap; }
      a { pointer-events: none; cursor: default; }
    </style></head>
    <body>
      <div class="container">
        <p>Hello,</p>
        <p>${SAMPLE_SUITES.length} suite executions for <strong>${SAMPLE.project}</strong> have finished.</p>
        <table class="meta">
          <tr><td>Executed By</td><td>${SAMPLE.executedBy}</td></tr>
          <tr><td>Initiated At</td><td>${SAMPLE.triggeredTime}</td></tr>
        </table>
        <div style="overflow-x:auto;">
        <table class="results">
          <tr><th>Suite Name</th><th>Execution Platform</th><th>Iteration</th><th>Duration</th><th>Status</th><th>Report</th></tr>
          ${rows}
        </table>
        </div>
        <p style="margin-top:20px;">Do not reply to this automated message.</p>
        <br>
        <p>Thank you,</p>
        <strong><span style="color:#3a89ff;">XPEER.AI</span></strong>
      </div>
    </body></html>`;
}

function buildModernPreview(): string {
  const statusColor = resolveStatusColor(SAMPLE.outcome);
  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <style>
      body { font-family: Arial, sans-serif; background-color: #eef2f7; margin: 0; padding: 24px; }
      .card { background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
      .banner { background-color: ${statusColor}; color: #ffffff; padding: 20px 24px; }
      .banner .badge { display: inline-block; padding: 4px 12px; border-radius: 999px; background-color: rgba(255,255,255,0.2); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
      .banner h2 { margin: 10px 0 0; font-size: 20px; }
      .body { padding: 24px; font-size: 15px; color: #222222; line-height: 1.6; }
      .button { display: inline-block; padding: 10px 22px; font-size: 15px; text-decoration: none; background-color: ${statusColor}; color: #ffffff; border-radius: 6px; margin-top: 8px; }
      a { pointer-events: none; cursor: default; }
    </style></head>
    <body>
      <div class="card">
        <div class="banner">
          <span class="badge">${SAMPLE.outcome}</span>
          <h2>${SAMPLE.suite}</h2>
        </div>
        <div class="body">
          <p>Hello,</p>
          <p>The execution for <strong>${SAMPLE.project}</strong> has finished in ${SAMPLE.duration}.</p>
          <p>Initiated at [${SAMPLE.triggeredTime}] by ${SAMPLE.executedBy}.</p>
          <a href="${SAMPLE.reportLink}" class="button">Open Report</a>
          <p style="margin-top:16px;">If the button doesn't work, copy this URL into your browser:</p>
          <h4 style="word-break: break-all;">${SAMPLE.reportLink}</h4>
          <p>Do not reply to this automated message.</p>
          <br>
          <p>Thank you,</p>
          <strong><span style="color:${statusColor};">XPEER.AI</span></strong>
        </div>
      </div>
    </body></html>`;
}

export function buildEmailTemplatePreviewHtml(templateType: EmailTemplateType): string {
  switch (templateType) {
    case 'MINIMAL':
      return buildMinimalPreview();
    case 'DETAILED':
      return buildDetailedPreview();
    case 'SIMPLE':
      return buildSimplePreview();
    case 'MODERN':
      return buildModernPreview();
    default:
      return buildDefaultPreview();
  }
}
